import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UserCheck, UserX, Clock, Users, Truck, Package, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserApprovalsPage = ({ user, token }) => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingVehicles, setPendingVehicles] = useState([]);
  const [pendingMaterials, setPendingMaterials] = useState([]);
  const [pendingStaff, setPendingStaff] = useState([]);
  const [counts, setCounts] = useState({ users: 0, vehicles: 0, materials: 0, staff: 0, total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAllPending();
    }
  }, []);

  const fetchAllPending = async () => {
    await Promise.all([
      fetchPendingUsers(),
      fetchPendingVehicles(),
      fetchPendingMaterials(),
      fetchPendingStaff(),
      fetchCounts()
    ]);
  };

  const fetchCounts = async () => {
    try {
      const response = await axios.get(`${API}/pending/counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCounts(response.data);
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/pending-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUsers(response.data);
    } catch (error) {
      console.error("Error fetching pending users:", error);
    }
  };

  const fetchPendingVehicles = async () => {
    try {
      const response = await axios.get(`${API}/pending/vehicle-numbers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingVehicles(response.data);
    } catch (error) {
      console.error("Error fetching pending vehicles:", error);
    }
  };

  const fetchPendingMaterials = async () => {
    try {
      const response = await axios.get(`${API}/pending/material-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingMaterials(response.data);
    } catch (error) {
      console.error("Error fetching pending materials:", error);
    }
  };

  const fetchPendingStaff = async () => {
    try {
      const response = await axios.get(`${API}/pending/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingStaff(response.data);
    } catch (error) {
      console.error("Error fetching pending staff:", error);
    }
  };

  const handleApproveUser = async (userId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/approve-user/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User approved!");
      fetchAllPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectUser = async (userId) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/auth/reject-user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User rejected");
      fetchAllPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveVehicle = async (id) => {
    setLoading(true);
    try {
      await axios.post(`${API}/pending/vehicle-numbers/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vehicle approved!");
      fetchAllPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectVehicle = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/pending/vehicle-numbers/${id}/reject`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vehicle rejected");
      fetchAllPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveMaterial = async (id) => {
    setLoading(true);
    try {
      await axios.post(`${API}/pending/material-types/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Material approved!");
      fetchAllPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectMaterial = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/pending/material-types/${id}/reject`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Material rejected");
      fetchAllPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveStaff = async (id) => {
    setLoading(true);
    try {
      await axios.post(`${API}/pending/staff/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Staff approved!");
      fetchAllPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectStaff = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/pending/staff/${id}/reject`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Staff rejected");
      fetchAllPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="approvals-page">
        <Card className="shadow-lg border-border/50">
          <CardContent className="flex flex-col items-center justify-center p-12 text-muted-foreground">
            <p className="text-lg">Access Denied. Admin privileges required.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="approvals-page">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading tracking-tight">Approvals</h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-1">
          Manage pending requests ({counts.total} total)
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3">
            <Users className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{counts.users}</p>
              <p className="text-xs text-muted-foreground">Users</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3">
            <Truck className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{counts.vehicles}</p>
              <p className="text-xs text-muted-foreground">Vehicles</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3">
            <Package className="w-8 h-8 text-orange-500" />
            <div>
              <p className="text-2xl font-bold">{counts.materials}</p>
              <p className="text-xs text-muted-foreground">Materials</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3">
            <User className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold">{counts.staff}</p>
              <p className="text-xs text-muted-foreground">Staff</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid w-full grid-cols-4 h-auto">
          <TabsTrigger value="users" className="gap-1 text-xs sm:text-sm py-2">
            <Users className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Users</span>
            {counts.users > 0 && <Badge className="ml-1 h-5 w-5 p-0 text-xs">{counts.users}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="vehicles" className="gap-1 text-xs sm:text-sm py-2">
            <Truck className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Vehicles</span>
            {counts.vehicles > 0 && <Badge className="ml-1 h-5 w-5 p-0 text-xs">{counts.vehicles}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="materials" className="gap-1 text-xs sm:text-sm py-2">
            <Package className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Materials</span>
            {counts.materials > 0 && <Badge className="ml-1 h-5 w-5 p-0 text-xs">{counts.materials}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="staff" className="gap-1 text-xs sm:text-sm py-2">
            <User className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Staff</span>
            {counts.staff > 0 && <Badge className="ml-1 h-5 w-5 p-0 text-xs">{counts.staff}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="shadow-lg border-border/50">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-500" />
                Pending User Registrations
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 sm:p-4 lg:p-6 sm:pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">Name</TableHead>
                      <TableHead className="text-xs sm:text-sm">Email</TableHead>
                      <TableHead className="text-xs sm:text-sm">Role</TableHead>
                      <TableHead className="text-xs sm:text-sm text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8 text-sm">
                          No pending users
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium text-xs sm:text-sm">{u.name}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{u.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize text-xs">{u.role}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1 sm:gap-2">
                              <Button onClick={() => handleApproveUser(u.id)} disabled={loading} size="sm" className="h-7 sm:h-8 text-xs bg-green-600 hover:bg-green-700">
                                <UserCheck className="w-3 h-3 sm:mr-1" />
                                <span className="hidden sm:inline">Approve</span>
                              </Button>
                              <Button onClick={() => handleRejectUser(u.id)} disabled={loading} variant="destructive" size="sm" className="h-7 sm:h-8 text-xs">
                                <UserX className="w-3 h-3 sm:mr-1" />
                                <span className="hidden sm:inline">Reject</span>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles">
          <Card className="shadow-lg border-border/50">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <Truck className="w-5 h-5 text-green-500" />
                Pending Vehicle Numbers
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 sm:p-4 lg:p-6 sm:pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">Vehicle Number</TableHead>
                      <TableHead className="text-xs sm:text-sm">Requested By</TableHead>
                      <TableHead className="text-xs sm:text-sm text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingVehicles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground py-8 text-sm">
                          No pending vehicles
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingVehicles.map((v) => (
                        <TableRow key={v.id}>
                          <TableCell className="font-medium text-xs sm:text-sm">{v.vehicle_number}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{v.requested_by_name}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1 sm:gap-2">
                              <Button onClick={() => handleApproveVehicle(v.id)} disabled={loading} size="sm" className="h-7 sm:h-8 text-xs bg-green-600 hover:bg-green-700">
                                <UserCheck className="w-3 h-3" />
                              </Button>
                              <Button onClick={() => handleRejectVehicle(v.id)} disabled={loading} variant="destructive" size="sm" className="h-7 sm:h-8 text-xs">
                                <UserX className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Materials Tab */}
        <TabsContent value="materials">
          <Card className="shadow-lg border-border/50">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-500" />
                Pending Material Types
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 sm:p-4 lg:p-6 sm:pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">Name</TableHead>
                      <TableHead className="text-xs sm:text-sm">Category</TableHead>
                      <TableHead className="text-xs sm:text-sm">Rate</TableHead>
                      <TableHead className="text-xs sm:text-sm">Requested By</TableHead>
                      <TableHead className="text-xs sm:text-sm text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingMaterials.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8 text-sm">
                          No pending materials
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingMaterials.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell className="font-medium text-xs sm:text-sm">{m.name}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{m.category}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{m.rate ? `₹${m.rate}` : '-'}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{m.requested_by_name}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1 sm:gap-2">
                              <Button onClick={() => handleApproveMaterial(m.id)} disabled={loading} size="sm" className="h-7 sm:h-8 text-xs bg-green-600 hover:bg-green-700">
                                <UserCheck className="w-3 h-3" />
                              </Button>
                              <Button onClick={() => handleRejectMaterial(m.id)} disabled={loading} variant="destructive" size="sm" className="h-7 sm:h-8 text-xs">
                                <UserX className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Tab */}
        <TabsContent value="staff">
          <Card className="shadow-lg border-border/50">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <User className="w-5 h-5 text-purple-500" />
                Pending Staff Data
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 sm:p-4 lg:p-6 sm:pt-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">Name</TableHead>
                      <TableHead className="text-xs sm:text-sm">Place</TableHead>
                      <TableHead className="text-xs sm:text-sm">Age</TableHead>
                      <TableHead className="text-xs sm:text-sm">Requested By</TableHead>
                      <TableHead className="text-xs sm:text-sm text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingStaff.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8 text-sm">
                          No pending staff
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingStaff.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell className="font-medium text-xs sm:text-sm">{s.name}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{s.place}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{s.age}</TableCell>
                          <TableCell className="text-xs sm:text-sm">{s.requested_by_name}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1 sm:gap-2">
                              <Button onClick={() => handleApproveStaff(s.id)} disabled={loading} size="sm" className="h-7 sm:h-8 text-xs bg-green-600 hover:bg-green-700">
                                <UserCheck className="w-3 h-3" />
                              </Button>
                              <Button onClick={() => handleRejectStaff(s.id)} disabled={loading} variant="destructive" size="sm" className="h-7 sm:h-8 text-xs">
                                <UserX className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserApprovalsPage;
